import React, { useState, useCallback } from 'react'
import { Auth } from 'aws-amplify'
import { ClientMetaData } from '@aws-amplify/auth/lib/types'

import StepWizard from 'react-step-wizard'
import { makeStyles } from '@material-ui/core'
import { v4 as uuidv4 } from 'uuid'

import StepOne from './StepOne'
import StepTwo from './StepTwo'
import StepThree from './StepThree'
import ConfirmSignUp from './ConfirmSignUp'
import Login from './Login'
import ForgotPassword from './ForgotPassword'
import ChangePassword from './ChangePassword'
import AcceptRequirements from './AcceptRequirements'

import { useGetPersonsByContact } from '../../hooks'
import { useErrorNotification } from 'civic-champs-shared/api/hooks'
import Loading from 'civic-champs-shared/core/Loading'
import { useGoogleAnalytics } from 'civic-champs-shared/utils/useGoogleAnalytics'

const useStyles = makeStyles(() => ({
  input: {
    width: '239px',
  },
  forgotLink: {
    marginTop: '20px',
    fontSize: '18px',
    cursor: 'pointer',
  },
}))

let endpointOverride: string = ''
const env = process.env.REACT_APP_ENV || 'dev'

const DEV_ENV = ['dev', 'development'].includes(env)
if (DEV_ENV) {
  endpointOverride = process.env.REACT_APP_NGROK_API_URL || ''
}

enum Steps {
  Initital = 1,
  ChooseUser,
  SignUp,
  ConfirmSignUp,
  Login,
  ResetPassword,
  ChangePassword,
  Final,
}

export interface GroupApplicationWizardFormProps {
  orgSlugName: string
  organization: any
  group: any
  currentUser: any
}

interface SignUpInfo {
  username: string
  password?: string
  email?: string
  phoneNumber?: string
  personId?: number
}

interface SignUpOptions {
  clientMetadata?: ClientMetaData
}

export default function GroupApplicationWizardForm(props: GroupApplicationWizardFormProps) {
  const { orgSlugName, organization, group, currentUser } = props
  const [personContacts, setPersonContacts] = useState([])
  const [matchedUser, setMatchedUser] = useState<any | null>({})
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [personId, setPersonId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(Steps.Initital)
  const [signUpInfo, setSignUpInfo] = useState<SignUpInfo>({ username: '', password: '' })

  const [getPersonsByContact] = useGetPersonsByContact()
  const showError = useErrorNotification()
  const classes = useStyles()
  const [trackEvent] = useGoogleAnalytics()

  const onSubmitStepOne = useCallback(
    async ({ email, phoneNumber }) => {
      try {
        setLoading(true)
        // @ts-ignore: convert hook file to ts
        const persons = await getPersonsByContact({ orgSlugName, groupId: group.id, email, phoneNumber })
        if (persons.length > 1) {
          setPersonContacts(persons)
          setCurrentStep(Steps.ChooseUser)
        } else if (persons.length === 1) {
          const [matchedUser] = persons
          if (matchedUser.cognitoSub) {
            setMatchedUser(matchedUser)
            setCurrentStep(Steps.Login)
          } else {
            setPersonId(matchedUser.id)
            setEmail(email)
            setPhoneNumber(phoneNumber)
            setCurrentStep(Steps.SignUp)
          }
        } else if (persons.length === 0) {
          setEmail(email)
          setPhoneNumber(phoneNumber)
          setCurrentStep(Steps.SignUp)
        }
      } catch (err) {
        showError('There was a problem fetching by contact', err)
      } finally {
        setLoading(false)
      }
    },
    [getPersonsByContact, group.id, orgSlugName, showError],
  )

  const onSubmitStepTwo = async (personId: number) => {
    if (personId > 0) {
      setMatchedUser(personContacts.find(({ id }) => id === personId))
      return Steps.Login
    } else {
      return Steps.SignUp
    }
  }

  const onResetPassword = useCallback(
    async ({ email, phoneNumber }) => {
      try {
        setLoading(true)
        await Auth.forgotPassword(email || phoneNumber)
        setCurrentStep(Steps.ChangePassword)
      } catch (err) {
        showError('There was a problem resetting your password', err)
      } finally {
        setLoading(false)
      }
    },
    [showError],
  )

  const onChangePassword = useCallback(
    async ({ contact, code, password }) => {
      try {
        setLoading(true)
        await Auth.forgotPasswordSubmit(contact, code, password)
        await Auth.signIn(contact, password)
        setCurrentStep(Steps.Login)
      } catch (err) {
        showError('There was a problem changing your password', err)
      } finally {
        setLoading(false)
      }
    },
    [showError],
  )

  const getSignUpOptions = (obj: any): SignUpOptions | undefined => {
    const filteredDict = Object.entries(obj).reduce(
      (acc, [key, value]) => ({ ...acc, ...(value ? { [key]: String(value) } : {}) }),
      {},
    )
    return Object.keys(filteredDict).length ? { clientMetadata: filteredDict } : undefined
  }

  const onSignUp = useCallback(
    async ({ givenName, familyName, email, phoneNumber, password, personId }) => {
      const username = uuidv4()
      const phoneNumberKey = email ? 'custom:alt_phone_number' : 'phone_number'

      const request = {
        username,
        password,
        attributes: {
          ...(email && { email }),
          ...(phoneNumber && { [phoneNumberKey]: phoneNumber }),
          given_name: givenName,
          family_name: familyName,
        },
        ...getSignUpOptions({ personId, endpointOverride }),
      }

      try {
        setLoading(true)
        await Auth.signUp(request)
        setSignUpInfo({ username, password, email, phoneNumber, personId })
        setCurrentStep(Steps.ConfirmSignUp)
        trackEvent('signup', { success: true })
      } catch (err) {
        showError('There was a problem creating your account', err)
        trackEvent('signup', { success: false })
      } finally {
        setLoading(false)
      }
    },
    [showError, trackEvent],
  )

  const onSignUpConfirm = useCallback(
    async ({ code }) => {
      setLoading(true)

      const { username, password, personId } = signUpInfo
      try {
        const options = getSignUpOptions({ personId, endpointOverride })

        await Auth.confirmSignUp(username, code, options)
        await Auth.signIn(username, password)
        setCurrentStep(Steps.Final)
        trackEvent('signupConfirm', { success: true })
      } catch (error) {
        showError('There was a problem signing in', error)
        trackEvent('signupConfirm', { success: false })
      } finally {
        setLoading(false)
      }
    },
    [signUpInfo, trackEvent, showError],
  )

  const onLogin = useCallback(
    async ({ email, phoneNumber, password }) => {
      try {
        setLoading(true)
        await Auth.signIn(email || phoneNumber, password)
        setCurrentStep(Steps.Final)
        trackEvent('login', { success: true })
      } catch (err) {
        setCurrentStep(Steps.Login)
        showError('There was a problem signing in', err)
        trackEvent('login', { success: false })
      } finally {
        setLoading(false)
      }
    },
    [showError, trackEvent],
  )

  if (loading) {
    return <Loading size={60} />
  }

  return (
    <div className="form-container">
      <StepWizard isLazyMount initialStep={currentUser?.id ? Steps.Final : currentStep}>
        <StepOne
          classes={classes}
          group={group}
          organization={organization}
          onSubmit={onSubmitStepOne}
          onCancel={async () => Steps.Login}
        />
        <StepTwo classes={classes} onSubmit={onSubmitStepTwo} persons={personContacts} />
        <StepThree classes={classes} email={email} phoneNumber={phoneNumber} personId={personId} onSubmit={onSignUp} />
        <ConfirmSignUp classes={classes} signUpInfo={signUpInfo} onSubmit={onSignUpConfirm} />
        <Login
          classes={classes}
          group={group}
          organization={organization}
          user={matchedUser}
          onSubmit={onLogin}
          onForgotPassword={async () => Steps.ResetPassword}
        />
        <ForgotPassword classes={classes} user={matchedUser} onSubmit={onResetPassword} />
        <ChangePassword classes={classes} user={matchedUser} onSubmit={onChangePassword} />
        <AcceptRequirements group={group} organization={organization} orgSlugName={orgSlugName} person={currentUser} />
      </StepWizard>
    </div>
  )
}
